import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, AutoComplete, Button, Form, Input, InputNumber, Modal, Select, Space, Spin, Table, message } from 'antd';
import {
  blobErrorText, createNonStdLabel, getNonStdPackagings, previewNonStdLabelsPdf, recordNonStdLabelsPrinted, updateNonStdLabel,
} from '../../services/nonstandardOps';
import { NonStdLabel, NonStdLabelGroupInfo, NonStdPackaging } from '../../types/nonstandardOps';
import { printPdfBlob } from '../../utils/printAgent';
import { groupFilterOption, hasGroups, normGroup } from './labelGroups';

/** 容器标签统一用 4x6 英寸；打印代理按任务类型（其次按尺寸）自动找对应的标签打印机 */
export const LABEL_JOB_TYPE = 'NONSTD_OP_LABEL';
export const LABEL_SIZE = { wIn: 4, hIn: 6 };

export type LabelModalMode = 'create' | 'edit' | 'print';

interface Props {
  open: boolean;
  instructionId: number;
  instructionCode?: string;
  /** create：新建；edit：修改已保存的标签；print：预览并打印已保存的标签（一张或多张） */
  mode: LabelModalMode;
  /** edit 传一张；print 传一张或多张 */
  labels?: NonStdLabel[];
  /** 未分组标签的下一个建议编号（只作快捷提示，不会自动填入） */
  nextNumber?: string;
  /** 本指令用过的标识组和每组的下一个建议编号 */
  groups?: NonStdLabelGroupInfo[];
  /** 最近一张标签用的组：新建标签时默认选它 */
  lastGroup?: string;
  /** 本指令全部标签：用来提示"同一个组里编号重复"（只提醒，仍可保存） */
  allLabels?: NonStdLabel[];
  onClose: () => void;
  /** 标签数据或打印次数变了，外层需要刷新 */
  onChanged: () => void;
}

const TITLES: Record<LabelModalMode, string> = { create: '新建标签', edit: '修改标签', print: '打印标签' };
const NUMBER_MAX = 20;
const CONTENTS_MAX = 300;
const REMARK_MAX = 500;
const GROUP_MAX = 30;

const toNum = (value: any): number | null => (value === null || value === undefined || value === '' ? null : Number(value));
const fmtDim = (value: number | null | undefined) => (value === null || value === undefined ? '?' : String(value));

const LabelPrintModal: React.FC<Props> = ({
  open, instructionId, instructionCode, mode, labels, nextNumber, groups, lastGroup, allLabels, onClose, onChanged,
}) => {
  const [group, setGroup] = useState('');
  const [number, setNumber] = useState('');
  const [contents, setContents] = useState('');
  const [remark, setRemark] = useState('');
  const [lengthCm, setLengthCm] = useState<number | null>(null);
  const [widthCm, setWidthCm] = useState<number | null>(null);
  const [heightCm, setHeightCm] = useState<number | null>(null);
  const [weightG, setWeightG] = useState<number | null>(null);
  const [packagingCode, setPackagingCode] = useState('');
  const [packagingName, setPackagingName] = useState('');
  const [packagings, setPackagings] = useState<NonStdPackaging[]>([]);
  const [copies, setCopies] = useState(1);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [busy, setBusy] = useState(false);
  const urlRef = useRef('');
  const seqRef = useRef(0);
  // 编号框里当前是不是"系统按组预填的编号"：是的话换组时可以跟着换，用户自己改过的就不动
  const prefilledRef = useRef('');

  const isPrintMode = mode === 'print';
  const labelIds = (labels || []).map(label => label.id);

  /** 某个组的下一个建议编号：已有的组取后端算好的；新组从 1 开始；未分组不预填 */
  const groupNext = (name: string) => {
    const clean = normGroup(name);
    if (!clean) return '';
    return (groups || []).find(item => item.name === clean)?.next_number || '1';
  };

  // 打开时按模式初始化输入
  useEffect(() => {
    if (!open) return;
    const first = (labels || [])[0];
    const editing = mode === 'edit' && first;
    const defaultGroup = mode === 'create' ? normGroup(lastGroup) : '';
    const prefill = mode === 'create' ? groupNext(defaultGroup) : '';
    prefilledRef.current = prefill;
    setGroup(editing ? first.group || '' : defaultGroup);
    setNumber(editing ? first.number : prefill);
    setContents(editing ? first.contents : '');
    setRemark(editing ? first.remark : '');
    setLengthCm(editing ? toNum(first.length_cm) : null);
    setWidthCm(editing ? toNum(first.width_cm) : null);
    setHeightCm(editing ? toNum(first.height_cm) : null);
    setWeightG(editing ? toNum(first.weight_g) : null);
    setPackagingCode(editing ? first.packaging_code || '' : '');
    setPackagingName(editing ? first.packaging_name || '' : '');
    setCopies(1);
    setPreviewError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, labelIds.join(',')]);

  // 预设包材：只在新建/修改时需要；取不到不影响手填尺寸
  useEffect(() => {
    if (!open || isPrintMode) return;
    let cancelled = false;
    getNonStdPackagings(instructionId)
      .then(list => { if (!cancelled) setPackagings(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setPackagings([]); });
    return () => { cancelled = true; };
  }, [open, isPrintMode, instructionId]);

  const revoke = () => {
    if (urlRef.current) {
      try { URL.revokeObjectURL(urlRef.current); } catch { /* ignore */ }
      urlRef.current = '';
    }
  };
  useEffect(() => revoke, []);

  // 预览：输入变化后稍等一下再向后端要 PDF（预览和打印是同一个生成函数，所见即所得）
  useEffect(() => {
    if (!open) { revoke(); setPreviewUrl(''); return undefined; }
    const seq = ++seqRef.current;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const blob = await previewNonStdLabelsPdf(
          instructionId,
          isPrintMode ? { label_ids: labelIds, copies: 1 } : { items: [{ group: normGroup(group), number, contents, remark }] },
        );
        if (seq !== seqRef.current) return;
        revoke();
        urlRef.current = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setPreviewUrl(urlRef.current);
        setPreviewError('');
      } catch (err: any) {
        if (seq !== seqRef.current) return;
        setPreviewError(await blobErrorText(err, '生成预览失败'));
      } finally {
        if (seq === seqRef.current) setPreviewLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, instructionId, isPrintMode, group, number, contents, remark, labelIds.join(',')]);

  /** 取最新 PDF（打印时间取此刻）→ 发给打印代理（不可用则浏览器打印）→ 记录打印次数 */
  const printLabels = useCallback(async (ids: number[]) => {
    const blob = await previewNonStdLabelsPdf(instructionId, { label_ids: ids, copies });
    const result = await printPdfBlob(blob, {
      jobType: LABEL_JOB_TYPE,
      labelSize: LABEL_SIZE,
      fileName: `nonstd_label_${instructionCode || instructionId}.pdf`,
      code: instructionCode,
    });
    if (result.channel === 'agent') {
      message.success(`已发送到打印代理${result.jobId ? `（任务 ${result.jobId}）` : ''}，标签正在打印。`);
    } else {
      message.warning('打印代理不可用，已改用浏览器打印：请在打印对话框里选择 4×6 英寸的标签打印机。', 6);
    }
    try {
      await recordNonStdLabelsPrinted(instructionId, { label_ids: ids, copies, channel: result.channel });
    } catch {
      message.warning('标签已打印，但记录打印次数失败（不影响打印）');
    }
  }, [instructionId, instructionCode, copies]);

  const run = async (action: () => Promise<void>, failText: string) => {
    setBusy(true);
    try {
      await action();
    } catch (err: any) {
      message.error(await blobErrorText(err, failText));
    } finally {
      setBusy(false);
    }
  };

  const payload = {
    group: normGroup(group),
    number: number.trim(),
    contents: contents.trim(),
    remark: remark.trim(),
    length_cm: lengthCm,
    width_cm: widthCm,
    height_cm: heightCm,
    weight_g: weightG,
    packaging_code: packagingCode,
    packaging_name: packagingName,
  };

  /** 保存（新建或修改）后可选立即打印；保存成功但打印失败时标签已在清单里，不会丢 */
  const saveThen = (alsoPrint: boolean) => run(async () => {
    const saved = mode === 'edit' && labels && labels[0]
      ? await updateNonStdLabel(labels[0].id, payload)
      : await createNonStdLabel(instructionId, payload);
    onChanged();
    if (!alsoPrint) {
      message.success('标签已保存，可以稍后在清单里打印');
      onClose();
      return;
    }
    try {
      await printLabels([saved.id]);
    } catch (err: any) {
      message.error(`标签已保存，但打印失败：${await blobErrorText(err, '请稍后在清单里重新打印')}`);
    }
    onChanged();
    onClose();
  }, '保存失败');

  const printOnly = () => run(async () => {
    await printLabels(labelIds);
    onChanged();
    onClose();
  }, '打印失败');

  const footer = isPrintMode ? [
    <Button key="cancel" onClick={onClose}>取消</Button>,
    <Button key="print" type="primary" loading={busy} disabled={!previewUrl || !!previewError} onClick={printOnly}>打印</Button>,
  ] : [
    <Button key="cancel" onClick={onClose}>取消</Button>,
    <Button key="save" loading={busy} onClick={() => saveThen(false)}>{mode === 'edit' ? '仅保存修改' : '仅保存，稍后打印'}</Button>,
    <Button key="print" type="primary" loading={busy} disabled={!previewUrl || !!previewError} onClick={() => saveThen(true)}>保存并打印</Button>,
  ];

  const onGroupChange = (value: string) => {
    setGroup(value);
    // 新建时：换组后把这个组的下一个编号预填到编号框；编号是用户自己改过的就不动
    if (mode === 'create' && (number === '' || number === prefilledRef.current)) {
      const next = groupNext(value);
      prefilledRef.current = next;
      setNumber(next);
    }
  };

  const onPackagingChange = (code?: string) => {
    if (!code) {
      setPackagingCode('');
      setPackagingName('');
      return;
    }
    const item = packagings.find(p => p.system_code === code);
    setPackagingCode(code);
    setPackagingName(item ? item.name : (code === packagingCode ? packagingName : ''));
    // 选了预设包材就自动填入长宽高（可以再手改）；重量要手填
    if (item) {
      if (item.length_cm !== null) setLengthCm(item.length_cm);
      if (item.width_cm !== null) setWidthCm(item.width_cm);
      if (item.height_cm !== null) setHeightCm(item.height_cm);
    }
  };

  const packagingOptions = [
    ...packagings.map(p => ({
      value: p.system_code,
      label: `${p.name}（${p.ownership_display} · ${fmtDim(p.length_cm)}×${fmtDim(p.width_cm)}×${fmtDim(p.height_cm)} cm）`,
    })),
    // 修改旧标签时，它当初选的包材如果已停用/不在列表里，仍然显示出来
    ...(packagingCode && !packagings.some(p => p.system_code === packagingCode)
      ? [{ value: packagingCode, label: packagingName || packagingCode }]
      : []),
  ];

  const editingId = mode === 'edit' && labels && labels[0] ? labels[0].id : 0;
  const duplicated = number.trim() !== '' && (allLabels || []).some(item => (
    item.id !== editingId && normGroup(item.group) === normGroup(group) && item.number === number.trim()
  ));
  const groupOptions = (groups || []).map(item => ({ value: item.name }));

  return (
    <Modal title={TITLES[mode]} open={open} onCancel={onClose} footer={footer} width={860} destroyOnClose maskClosable={false}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 340px', minWidth: 300 }}>
          {isPrintMode ? (
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              scroll={{ y: 260 }}
              dataSource={labels || []}
              columns={[
                ...(hasGroups(labels || []) ? [{ title: '标识组', dataIndex: 'group', width: 100, render: (v: string) => v || '-' }] : []),
                { title: '编号', dataIndex: 'number', width: 70, render: (v: string) => v || '-' },
                { title: '内容物', dataIndex: 'contents', render: (v: string) => v || '-' },
                { title: '备注', dataIndex: 'remark', render: (v: string) => v || '-' },
              ]}
            />
          ) : (
            <Form layout="vertical">
              <Form.Item
                label="标识组"
                extra="用来区分同一个指令里不同批次/类别的容器，例如“LPN扫描”“翻新完成A类”；会打印在标签最上面一行，可以留空。可以选已有的组，也可以直接输入新的组名。"
              >
                <AutoComplete
                  value={group}
                  options={groupOptions}
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="选择已有的组，或输入新的组名"
                  filterOption={groupFilterOption((groups || []).map(item => item.name))}
                  onChange={(value: string) => onGroupChange(String(value ?? '').slice(0, GROUP_MAX))}
                >
                  <Input maxLength={GROUP_MAX} />
                </AutoComplete>
              </Form.Item>
              <Form.Item
                label="数字编号"
                validateStatus={duplicated ? 'warning' : undefined}
                help={duplicated ? `${normGroup(group) ? `“${normGroup(group)}”组` : '未分组'}里已经有编号 ${number.trim()} 的标签了，仍然可以保存` : undefined}
                extra={!duplicated ? (
                  mode === 'create' && nextNumber && !number && !normGroup(group)
                    ? <a onClick={() => setNumber(nextNumber)}>使用下一个编号 {nextNumber}</a>
                    : (normGroup(group) ? '按所选组自动预填了下一个编号，可以修改或清空；标签上最醒目的大字' : '标签上最醒目的大字，可以留空')
                ) : undefined}
              >
                <Input
                  value={number}
                  maxLength={NUMBER_MAX}
                  placeholder="例如 12"
                  allowClear
                  onChange={e => setNumber(e.target.value)}
                />
              </Form.Item>
              <Form.Item label="内容物" extra="这个容器里放的是什么，例如“可售：蓝色T恤”；可以留空">
                <Input.TextArea value={contents} rows={3} maxLength={CONTENTS_MAX} showCount onChange={e => setContents(e.target.value)} />
              </Form.Item>
              <Form.Item label="备注" extra="可以留空">
                <Input.TextArea value={remark} rows={2} maxLength={REMARK_MAX} showCount onChange={e => setRemark(e.target.value)} />
              </Form.Item>
              <Form.Item
                label="尺寸与重量（选填）"
                extra="只用于记录，不会打印在标签上。可以先选一个预设包材自动填入长宽高（仍可手改），重量要手填。"
                style={{ marginBottom: 0 }}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%', marginBottom: 8 }}
                  placeholder="选择预设包材（可不选）"
                  value={packagingCode || undefined}
                  options={packagingOptions}
                  notFoundContent="没有可选的预设包材"
                  onChange={(value?: string) => onPackagingChange(value)}
                />
                <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
                  <InputNumber style={{ width: '33%' }} min={0.01} max={99999.99} precision={2} controls={false} placeholder="长" addonAfter="cm" value={lengthCm} onChange={v => setLengthCm(v === null ? null : Number(v))} />
                  <InputNumber style={{ width: '33%' }} min={0.01} max={99999.99} precision={2} controls={false} placeholder="宽" addonAfter="cm" value={widthCm} onChange={v => setWidthCm(v === null ? null : Number(v))} />
                  <InputNumber style={{ width: '34%' }} min={0.01} max={99999.99} precision={2} controls={false} placeholder="高" addonAfter="cm" value={heightCm} onChange={v => setHeightCm(v === null ? null : Number(v))} />
                </Space.Compact>
                <InputNumber style={{ width: '100%' }} min={0.01} max={9999999999.99} precision={2} controls={false} placeholder="重量" addonAfter="g" value={weightG} onChange={v => setWeightG(v === null ? null : Number(v))} />
              </Form.Item>
            </Form>
          )}
          <Space align="center" style={{ marginTop: 8 }}>
            <span>每张打印</span>
            <InputNumber min={1} max={20} precision={0} value={copies} onChange={value => setCopies(Math.max(1, Math.min(20, Number(value) || 1)))} />
            <span>份</span>
          </Space>
          <div style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c', lineHeight: 1.7 }}>
            标签上自动带有：指令编码、所属项目、操作人（当前账号）、打印日期时间，这几项不能修改。预览与实际打印一致（打印时间以点“打印”那一刻为准）。
          </div>
        </div>
        <div style={{ flex: '0 0 300px' }}>
          <div style={{ position: 'relative', width: 300, height: 450, border: '1px solid #d9d9d9', background: '#fafafa' }}>
            {previewUrl ? <iframe title="label-preview" src={`${previewUrl}#toolbar=0&navpanes=0&view=Fit`} style={{ width: '100%', height: '100%', border: 0 }} /> : null}
            {previewLoading ? (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)' }}>
                <Spin />
              </div>
            ) : null}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c', textAlign: 'center' }}>预览（4×6 英寸）</div>
        </div>
      </div>
      {previewError ? <Alert type="error" showIcon style={{ marginTop: 12 }} message={previewError} /> : null}
    </Modal>
  );
};

export default LabelPrintModal;
